import { useState, useEffect } from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  Breadcrumb, 
  BreadcrumbItem, 
  BreadcrumbLink,
  Container,
  Spinner,
  Center,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@chakra-ui/icons';
import TicketForm from '../../components/tickets/TicketForm';
import ticketService from '../../services/ticketService';
import { useAuth } from '../../contexts/AuthContext';

const EditTicket = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  
  const [ticket, setTicket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  // Prevent support users from accessing edit page
  useEffect(() => {
    if (user?.role === 'support') {
      setUnauthorized(true);
      setIsLoading(false);
      toast({
        title: 'Access Denied',
        description: 'Support users cannot modify tickets.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // Redirect after 3 seconds
      const timer = setTimeout(() => {
        navigate('/tickets');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [user, navigate, toast]);

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        setIsLoading(true);
        const response = await ticketService.getTicket(id);
        setTicket(response.data.data);
      } catch (error) {
        toast({
          title: 'Error fetching ticket',
          description: error.response?.data?.message || 'Something went wrong',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/tickets');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTicket();
  }, [id, navigate, toast]);

  if (unauthorized) {
    return (
      <Alert
        status="error"
        variant="subtle"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        height="200px"
      >
        <AlertIcon boxSize="40px" mr={0} />
        <AlertTitle mt={4} mb={1} fontSize="lg">
          Access Denied
        </AlertTitle>
        <AlertDescription maxWidth="sm">
          Support users cannot modify tickets. Redirecting to tickets list...
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Center h="200px">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="brand.500"
          size="xl"
        />
      </Center>
    );
  }

  if (!ticket) {
    return null;
  }

  return (
    <Box>
      <Breadcrumb 
        spacing="8px" 
        separator={<ChevronRightIcon color="gray.500" />}
        mb={6}
      >
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/dashboard">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/tickets">Tickets</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to={`/tickets/${id}`}>Ticket Details</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Edit Ticket</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Heading size="lg" mb={2}>Edit Ticket</Heading>
      <Text color="gray.600" mb={6}>
        Update your support ticket details below.
      </Text>

      <Container maxW="container.md" p={0}>
        <TicketForm ticket={ticket} isEditing={true} />
      </Container>
    </Box>
  );
};

export default EditTicket;
